<template>
  <div v-if="currentCrm" class="container-fluid account-crm-settings">
    <div class="account-crm-settings__header">
      <img
        :src="imageUrl"
        :alt="crm"
        class="account-crm-settings__logo"
      />
      <div class="account-crm-settings__title">{{ displayName }} CRM</div>
      <div class="account-crm-settings__subtitle">{{ details.moto }}</div>
      <div class="account-crm-settings__badge">
        <snappy-badge :background="isActive ? 'green' : 'grey'">{{
          isActive ? 'Connected' : currentCrm.status
        }}</snappy-badge>
      </div>
      <div class="account-crm-settings__actions" v-if="isActive">
        <snappy-dropdown text="Actions" inverted :items="filteredActions"></snappy-dropdown>
        <a href="/integrations/catalogue/disconnect" class="btn btn-lg btn-danger">Disconnect</a>
      </div>

      <span v-else v-tooltip="disableConnectionTooltip">
        <button class="btn btn-lg btn-primary" :class="{disabled: isNotConnected || !crmIsAvailable}" @click="connect">
          Connect
        </button>
      </span>
    </div>
    <div class="account-crm-settings__links">
      <a
        v-for="link of links"
        :key="link.text"
        :href="link.href"
        :target="link.target"
        v-hubspot-widget="link.widget"
        class="btn btn-lg btn-primary account-crm-settings__link"
      >
        <i :class="link.iconClass"></i>
        {{ link.text }}
      </a>
    </div>
    <div class="account-crm-settings-box d-none">
      <div class="account-crm-settings-box__header">
        <div class="account-crm-settings-box__title">Your {{ currentCrm.commercialName }} Secret Key</div>
      </div>
      <div class="account-crm-settings-box__key">
        <span v-if="isActive && activeCrmData.settings">{{ activeCrmData.settings.secret }}</span>
        <span v-else>Please connect your CRM first.</span>
      </div>
    </div>
    <div
      v-if="syncAvailable"
      class="account-crm-settings-box"
      :class="{'account-crm-settings-box--disabled': !isActive || nightlyImport || !syncAvailable}"
    >
      <div class="account-crm-settings-box__header">
        Automatic Nightly Sync

        <div class="account-crm-settings-box__switch">
          <snappy-switch v-model="nightlySync" id="nightlySync"></snappy-switch>
          <snappy-badge v-if="noSyncGroupsSelected" background="red">No groups selected</snappy-badge>
          <snappy-badge v-if="nightlySyncGroups && nightlySyncGroups.length" background="green">{{nightlySyncGroups.length}} groups selected</snappy-badge>

        </div>

        <div v-if="isActive && lastSync" class="account-crm-settings-box__date">Last Sync: {{ lastSync }}</div>
      </div>
      <div class="account-crm-settings-box__text">
        With this setting enabled, all contacts in the selected groups will be bi-directionally synced between Snappy
        Kraken and the CRM at 12am ET. If a contact was updated in both {{ platformTitle }} and the CRM, the update made
        in {{ platformTitle }} will be preserved.
      </div>
      <a
        class="account-crm-settings-box__link"
        :class="{
          'account-crm-settings__link--active': noSyncGroupsSelected,
        }"
        @click="syncModalOpen = true"
        >Select groups to sync</a
      >
    </div>
    <div class="account-crm-settings-box" :class="{'account-crm-settings-box--disabled': !isActive || nightlySync}">
      <div class="account-crm-settings-box__header">
        Automatic Nightly Import
        <div class="account-crm-settings-box__switch">
          <snappy-switch v-model="nightlyImport" id="nightlyImport"></snappy-switch>
          <snappy-badge v-if="noImportGroupsSelected" background="red">No groups selected</snappy-badge>
          <snappy-badge v-if="nightlyImportGroups && nightlyImportGroups.length" background="green">{{nightlyImportGroups.length}} groups selected</snappy-badge>
        </div>
      </div>
      <div class="account-crm-settings-box__text">
        With this setting enabled, all contacts in the selected groups will be synced from the CRM to
        {{ platformTitle }} at 12am ET.
      </div>
      <a
        class="account-crm-settings-box__link"
        :class="{
          'account-crm-settings__link--active': noImportGroupsSelected,
        }"
        @click="importModalOpen = true"
        >Select groups to import</a
      >
    </div>
    <template v-if="isSalesforce">
      <div class="account-crm-settings-box" :class="{'account-crm-settings-box--disabled': !isActive}">
        <div>
          <div class="account-crm-settings-box__header">
            Send Leads to CRM
            <snappy-switch v-model="sendLeads" id="sendLeads"></snappy-switch>
          </div>
        </div>
        <div
            v-if="available.crm_settings.data.find(item => item.name === 'salesforce') && available.crm_settings.data.find(item => item.name === 'salesforce').send_leads_description"
            class="account-crm-settings-box__text"
            v-text="available.crm_settings.data.find(item => item.name === 'salesforce').send_leads_description"
        />
        <div v-else class="account-crm-settings-box__text">
          With this setting enabled any contacts that are added to {{ platformTitle }} via a campaign form submission
          will be automatically synced to a Salesforce as new leads. The sync will occur within 30 minutes after the
          form submission.
        </div>
      </div>
    </template>
    <flow-modal v-if="syncModalOpen" @close="syncModalOpen = false">
      <groups-selector
        :groups="localGroups"
        :excluded-group="excludedGroup"
        heading="Select groups for automatic sync"
        :preselectedGroups="nightlySyncGroups"
        @selected="onSelectGroups"
      >
        <template v-slot:subheading>
          Select the {{ platformTitle }} that you want to sync to the CRM on a nightly basis. If you want to select
          groups from the CRM import to {{ platformTitle }} on a nightly basis.
          <a
            href="#"
            class="account-crm-settings-box__link"
            @click.prevent="
              syncModalOpen = false;
              importModalOpen = true;
            "
            >Click here</a
          >
        </template>
        <button type="button" class="btn btn-lg btn-outline-primary mr-2" @click="syncModalOpen = false">Cancel</button>
        <button type="button" class="btn btn-lg btn-primary" @click="saveNightlySyncGroups">Save</button>
      </groups-selector>
    </flow-modal>

    <flow-modal v-if="importModalOpen" @close="importModalOpen = false">
      <groups-selector
        :groups="crmGroups"
        heading="Select groups for automatic import"
        :preselectedGroups="nightlyImportGroups"
        @selected="onSelectGroups"
      >
        <template v-slot:subheading>
          Select the CRM groups you want to import to {{ platformTitle }} on a nightly basis.
        </template>
        <button type="button" class="btn btn-lg btn-outline-primary mr-2" @click="importModalOpen = false">
          Cancel
        </button>
        <button type="button" class="btn btn-lg btn-primary" @click="saveNightlyImportGroups">Save</button>
      </groups-selector>
    </flow-modal>
  </div>
</template>

<script>
import SnappyBadge from '../components/SnappyBadge.vue';
import SnappyDropdown from '../components/SnappyDropdown.vue';
import SnappySwitch from '../components/SnappySwitch.vue';
import IntegrationsFooter from '../components/integrations/IntegrationsFooter.vue';
import {crmsData} from '../components/integrations/crmsData.js';
import dateFormatter from '../mixins/dateFormatter';
import FlowModal from '../components/shared/FlowModal.vue';
import GroupsSelector from '../components/shared/GroupsSelector.vue';
import {getConnectLink} from '../components/integrations/getConnectLink';
import {HubspotWidget} from '../directives/HubspotWidget';


export default {
  name: 'account-crm-settings-page',
  components: {
    SnappyBadge,
    SnappyDropdown,
    SnappySwitch,
    IntegrationsFooter,
    FlowModal,
    GroupsSelector,
  },
  mixins: [dateFormatter],
  directives: {HubspotWidget},
  data() {
    return {
      actions: [
        {text: 'Import', href: '/import/select-groups'},
        {text: 'Import History', href: '/inventory/contacts/imports'},
        {text: 'Sync (2-way)', href: '/sync/select-groups'},
        {text: 'Sync History', href: '/sync/management'},
      ],
      cards: null,
      syncModalOpen: false,
      nightlySync: false,
      nightlySyncGroups: [],
      nightlySyncTouched: false,
      importModalOpen: false,
      nightlyImport: false,
      nightlyImportGroups: [],
      nightlyImportTouched: false,
      sendLeads: false,
      syncEndpoint: '/api/?entity=vessel&action=updateCrmSettings',
      selectedGroups: null,
    };
  },
  props: {
    crm: {
      type: String,
    },
    activeCrm: {
      type: String,
    },
    localGroups: {
      type: String,
      required: true,
    },
    crmGroups: {
      type: String,
      required: true,
    },
    timezone: {
      type: String,
    },
    availability: {
      type: String,
    },
    platformTitle: {
      type: String,
      required: true,
    },
    excludedGroup: {
      type: String,
    },
  },
  computed: {
    noSyncGroupsSelected() {
      return this.nightlySyncTouched && !this.nightlySyncGroups?.length;
    },
    noImportGroupsSelected() {
      return this.nightlyImportTouched && !this.nightlyImportGroups?.length;
    },
    isSalesforce() {
      return this.crm === 'salesforce';
    },
    activeCrmData() {
      return JSON.parse(this.activeCrm);
    },
    currentCrm() {
      return this.cards?.find(item => item.name === this.crm);
    },
    details() {
      const {details} = this.currentCrm;
      return details;
    },
    isActive() {
      return this.activeCrmData.crm_name === this.crm;
    },
    isNotConnected() {
      return this.currentCrm.status === 'disabled' || (Object.keys(this.activeCrmData)?.length && !this.isActive);
    },
    lastSync() {
      if (!this.activeCrmData.last_sync_at) return;
      return `${this.dateLarge(new Date(this.activeCrmData.last_sync_at), this.timezone)} by me`;
    },
    available() {
      return JSON.parse(this.availability);
    },
    crmIsAvailable() {
      return !!this.available[this.crm + '_available'];
    },
    syncAvailable() {
      return !!this.available['two_way_sync'];
    },
    disableConnectionTooltip() {
      return this.isNotConnected
        ? 'You have another CRM connected. Please disconnect it first.'
        : !this.crmIsAvailable
        ? 'This CRM is not supported on your platform'
        : '';
    },
    filteredActions() {
      return !this.syncAvailable ? this.actions.filter(action => !action.href.includes('/sync/')) : this.actions;
    },
    links() {
      return [
        {
          href: this.details.link,
          iconClass: 'fas fa-video mr-3',
          text: 'Learn',
          target: '_blank',
        },
        {
          widget: true,
          iconClass: 'fas fa-question-circle mr-3',
          text: 'Get some help',
        },
        {
          href: '/account/troubleshooting/',
          iconClass: 'fas fa-exclamation-triangle mr-3',
          text: 'Troubleshooting'
        },
      ];
    },
    displayName() {
      return this.currentCrm.commercialName;
    },
    imageUrl() {
      return `/wp-content/themes/snappy-theme/assets/images/crms/${this.currentCrm.name}.svg`;
    }
  },
  created() {
    this.cards = crmsData(this.platformTitle);
    if (this.isActive) {
      this.nightlySync = !!this.activeCrmData.settings['nightly-sync'];
      this.nightlyImport = !!this.activeCrmData.settings['nightly-import'];
      this.sendLeads = !!this.activeCrmData.settings['export-leads'];
      this.nightlySyncGroups = this.activeCrmData.settings['nightly-sync-groups'];
      this.nightlyImportGroups = this.activeCrmData.settings['nightly-import-groups'];
    }
  },
  methods: {
    connect() {
      getConnectLink(this.crm);
    },
    onSelectGroups(groups) {
      this.selectedGroups = groups;
      if(groups?.length) {
        this.nightlySyncTouched = false;
        this.nightlyImportTouched = false;
      }
    },
    saveNightlyImportGroups() {
      this.saveFeatureToDB('nightly-import-groups', this.selectedGroups);
      this.nightlyImportGroups = this.selectedGroups;
      this.importModalOpen = false;
      this.nightlyImportTouched = true;
      if(this.noImportGroupsSelected) {
        this.nightlyImport = false;
      }
    },
    saveNightlySyncGroups() {
      this.saveFeatureToDB('nightly-sync-groups', this.selectedGroups);
      this.nightlySyncGroups = this.selectedGroups;
      this.syncModalOpen = false;
      this.nightlySyncTouched = true;
      if(this.noSyncGroupsSelected) {
        this.nightlySync = false;
      }
    },
    saveFeatureToDB(feature, val) {
      return axios.post(this.syncEndpoint, {[feature]: val});
    },
  },
  watch: {
    nightlySync(val) {
      if(val) {
        this.nightlySyncTouched = true;
      }
      if (val && this.noSyncGroupsSelected) {
        $.notify(
          'It looks like you have enabled Nightly Sync, but there are no groups in it selected. Please go to Nightly Sync Settings and pick the groups you want to have synced - or disable the feature.',
          'error'
        );

        setTimeout(() => {
          this.nightlySync = false;
        }, 1000);
      }

      this.saveFeatureToDB('nightly-sync', val)
          .catch((error) => {
            $.notify(error.response.data.message, 'error');
            setTimeout(() => this.nightlySync = !val, 1000);
          });
    },
    nightlyImport(val) {
      if(val) {
        this.nightlyImportTouched = true;
      }
      if (val && this.noImportGroupsSelected) {
        $.notify(
          'It looks like you have enabled Nightly Import, but there are no groups in it selected. Please go to Nightly Import Settings and pick the groups you want to have imported - or disable the feature.',
          'error'
        );
        setTimeout(() => {
          this.nightlyImport = false;
        }, 1000);
      }
      this.saveFeatureToDB('nightly-import', val)
          .catch((error) => {
            $.notify(error.response.data.message, 'error');
            setTimeout(() => this.nightlyImport = !val, 1000);
          });
    },
    sendLeads(val) {
      this.saveFeatureToDB('export-leads', val);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../sass/web/_variables.scss';

.account-crm-settings {
  a {
    text-decoration: none !important;
  }
  &__header {
    background: $white-color;
    min-height: 144px;
    padding: 16px;
    box-shadow: inset 0px -1px 0px $platinum-color;
    display: grid;
    grid-template-columns: 1fr min-content;
    grid-template-areas:
      'logo badge'
      'title title'
      'subtitle subtitle'
      'actions actions';
    @media (min-width: $breakpoint-md) {
      padding: 24px;
      grid-template-columns: 110px 1fr min-content;
      grid-template-rows: min-content min-content;
      grid-template-areas:
        'logo title badge'
        'logo subtitle actions';
    }
  }

  &__title,
  &__subtitle {
    text-align: left;
    font-weight: bold;
  }
  &__title {
    font-size: 32px;
    line-height: 52px;
    color: $mine-shaft-color;
    grid-area: title;
    @media (min-width: $breakpoint-md) {
      font-size: 48px;
    }
  }
  &__subtitle {
    font-size: 16px;
    align-self: flex-end;
    grid-area: subtitle;
    margin-bottom: 20px;
    @media (min-width: $breakpoint-md) {
      font-size: 20px;
      margin-bottom: 0;
    }
  }
  &__logo {
    width: 72px;
    height: 72px;
    grid-area: logo;
    @media (min-width: $breakpoint-md) {
      width: 96px;
      height: 96px;
    }
  }
  &__badge {
    text-align: right;
    grid-area: badge;
  }
  &__actions {
    display: inline-flex;
    align-items: flex-end;
    gap: 10px;
    grid-area: actions;
  }
  &__links {
    margin: 16px 0 8px;
    display: flex;
    gap: 16px;
  }
  &__link {
    flex: 1;
    font-weight: 700;
    display: block;
    margin-bottom: 8px;
    &--active {
      animation: blinker 1.5s linear infinite;
    }
  }
  &-box {
    min-height: 120px;
    padding: 24px;
    text-align: left;
    background: $white-color;
    border: 1px solid $platinum-color;
    border-radius: 8px;
    margin-bottom: 16px;
    &--disabled {
      filter: grayscale(1) brightness(0.7);
      pointer-events: none;
    }
    &__header {
      font-size: 24px;
      color: $mine-shaft-color;
      display: grid;
      gap: 20px;
      margin-bottom: 10px;
      @media (min-width: $breakpoint-md) {
        grid-template-columns: max-content 1fr max-content;
        grid-template-rows: 1fr;
        gap: 10px;
      }
    }
    &__switch {
      display: flex;
      align-items: center;
      gap: 30px;
    }
    &__title {
      font-weight: 700;
    }
    &__key {
      color: $mine-shaft-color;
    }
    &__text {
      color: $dove-grey-color;
    }
    &__date {
      background: $color-iceberg;
      font-size: 12px;
      padding: 8px 12px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
    }
    &__link {
      color: $half-baked-color;
      cursor: pointer;
    }
  }
}

@keyframes blinker {
  50% {
    opacity: 0.1;
  }
}
</style>
